import { localISODate, toISODate } from "@/lib/date-range";

/**
 * Фильтр списка заказов по дате отгрузки.
 *
 * Пресеты хранятся ОТНОСИТЕЛЬНО (`today` / `tomorrow`), а не датой: оператор
 * выбрал «Сегодня» вечером — утром «Сегодня» должно значить уже новый день, а
 * не вчерашнюю дату из localStorage. Дата из календаря хранится как есть (ISO).
 * В ручку уходит всегда конкретная дата — `resolveShipFilter`, по локальному
 * времени (не UTC: ночью по Москве UTC ещё во вчерашнем дне).
 */
export type ShipPreset = "today" | "tomorrow";
/** `""` — фильтра нет; пресет; или конкретная дата `yyyy-mm-dd`. */
export type ShipFilter = "" | ShipPreset | string;

const PRESET_OFFSET: Record<ShipPreset, number> = { today: 0, tomorrow: 1 };

/** Настоящая календарная дата `yyyy-mm-dd` (не «2026-02-31»). */
export function isISODate(s: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = s.split("-").map(Number) as [number, number, number];
  return toISODate(new Date(y, m - 1, d)) === s;
}

/** Значение из localStorage → фильтр. Мусор (ручная правка, старый формат)
 *  отбрасывается: это удобство одного зрителя, а не данные. */
export function parseStoredShipFilter(raw: string): ShipFilter {
  if (raw === "today" || raw === "tomorrow") return raw;
  return isISODate(raw) ? raw : "";
}

/** Фильтр → дата для ручки (`ship_date`), `""` — без фильтра. */
export function resolveShipFilter(f: ShipFilter): string {
  if (f === "today" || f === "tomorrow") return localISODate(PRESET_OFFSET[f]);
  return f;
}

/** «23.10», с годом — если не текущий. */
export function fmtShortDate(iso: string): string {
  const [y, m, d] = iso.split("-");
  return Number(y) === new Date().getFullYear() ? `${d}.${m}` : `${d}.${m}.${y}`;
}

/** Подпись чипа над списком: «Отгрузка: сегодня, 21.10» / «Отгрузка: 23.10». */
export function shipFilterLabel(f: ShipFilter): string {
  const iso = resolveShipFilter(f);
  if (f === "today") return `Отгрузка: сегодня, ${fmtShortDate(iso)}`;
  if (f === "tomorrow") return `Отгрузка: завтра, ${fmtShortDate(iso)}`;
  return `Отгрузка: ${fmtShortDate(iso)}`;
}
